/** The scheduled-update wizard's draft, its per-step validation and conversion to ReportInput. */
import type { Frequency, Report, ReportInput } from '@/api/types';
import { ordinal, WEEKDAYS } from '@/lib/format';

export interface ReportDraft {
  name: string;
  allWidgets: boolean;
  widget_ids: string[];
  frequency: Frequency;
  time_of_day: string;
  weekday: number | null;
  day_of_month: number | null;
  timezone: string;
  destination_ids: string[];
  enabled: boolean;
}

export const STEPS = ['What', 'How often', 'Where'] as const;
export type Step = 0 | 1 | 2;

export function emptyDraft(timezone: string): ReportDraft {
  return {
    name: '',
    allWidgets: true,
    widget_ids: [],
    frequency: 'daily',
    time_of_day: '08:00',
    weekday: 1,
    day_of_month: 1,
    timezone,
    destination_ids: [],
    enabled: true,
  };
}

export function fromReport(r: Report): ReportDraft {
  return {
    name: r.name,
    allWidgets: r.widget_ids.length === 0,
    widget_ids: r.widget_ids,
    frequency: r.frequency,
    time_of_day: r.time_of_day,
    weekday: r.weekday ?? 1,
    day_of_month: r.day_of_month ?? 1,
    timezone: r.timezone,
    destination_ids: r.destination_ids,
    enabled: r.enabled,
  };
}

export function validateStep(step: Step, d: ReportDraft): Record<string, string> {
  const e: Record<string, string> = {};
  if (step === 0) {
    if (!d.name.trim()) e.name = 'Give this update a name.';
    else if (d.name.trim().length > 80) e.name = 'Keep the name under 80 characters.';
    if (!d.allWidgets && d.widget_ids.length === 0) e.widget_ids = 'Pick at least one KPI, or choose “Every KPI on my dashboard”.';
  }
  if (step === 1) {
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(d.time_of_day)) e.time_of_day = 'Use a time like 08:00.';
    if (d.frequency === 'weekly' && !(d.weekday && d.weekday >= 1 && d.weekday <= 7)) e.weekday = 'Pick a day of the week.';
    if (d.frequency === 'monthly' && !(d.day_of_month && d.day_of_month >= 1 && d.day_of_month <= 28))
      e.day_of_month = 'Pick a day between 1 and 28.';
    if (!d.timezone) e.timezone = 'Pick a time zone.';
  }
  if (step === 2) {
    if (d.destination_ids.length === 0) e.destination_ids = 'Pick at least one place to send it.';
  }
  return e;
}

export function validateAll(d: ReportDraft): { step: Step; errors: Record<string, string> } | null {
  for (const step of [0, 1, 2] as Step[]) {
    const errors = validateStep(step, d);
    if (Object.keys(errors).length) return { step, errors };
  }
  return null;
}

export function toInput(d: ReportDraft): ReportInput {
  return {
    name: d.name.trim(),
    frequency: d.frequency,
    time_of_day: d.time_of_day,
    weekday: d.frequency === 'weekly' ? d.weekday : null,
    day_of_month: d.frequency === 'monthly' ? d.day_of_month : null,
    timezone: d.timezone,
    widget_ids: d.allWidgets ? [] : d.widget_ids,
    destination_ids: d.destination_ids,
    enabled: d.enabled,
  };
}

export function scheduleSummary(r: Pick<Report, 'frequency' | 'time_of_day' | 'weekday' | 'day_of_month'>): string {
  if (r.frequency === 'daily') return `Every day at ${r.time_of_day}`;
  if (r.frequency === 'weekly') return `Every ${WEEKDAYS[(r.weekday ?? 1) - 1] ?? 'Monday'} at ${r.time_of_day}`;
  return `Monthly on the ${ordinal(r.day_of_month ?? 1)} at ${r.time_of_day}`;
}

export const COVERS: Record<Frequency, string> = {
  daily: 'Yesterday compared with the day before.',
  weekly: 'The last 7 days compared with the 7 before.',
  monthly: 'The last 30 days compared with the 30 before.',
};
